import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Terminal, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { fetchCommandTypes, fetchDevicePosition, fetchDevices, sendCommand, updateDevice, type Device } from '@/lib/traccar-api';
import { fetchDeviceStockPaged, fetchSimCard, logCommand, type DeviceStock } from '@/lib/reseller-api';

const COMMANDS_WITH_DATA = ['custom', 'setServer'];
const COMMANDS_WITH_FREQUENCY = ['positionPeriodic'];
const COMMANDS_WITH_PORT = ['outputControl'];
const LAST_COMMAND_DEVICE_KEY = 'last-command-device-id';

const TELTONIKA_PRESETS = [
  { label: 'getinfo', command: 'getinfo' },
  { label: 'getver', command: 'getver' },
  { label: 'ggps', command: 'ggps' },
  { label: 'getgps', command: 'getgps' },
  { label: 'getrecord', command: 'getrecord' },
  { label: 'cpureset', command: 'cpureset' },
];

const isTeltonikaModel = (model: string): boolean => /^(fmb|fmt|fmc|fmm|fm[0-9])/.test(model);

const isTeltonikaStockDevice = (stockDevice?: DeviceStock): boolean => {
  const manufacturer = stockDevice?.manufacturer?.toLowerCase().trim() ?? '';
  const model = stockDevice?.model?.toLowerCase().trim() ?? '';
  return manufacturer.includes('teltonika') || isTeltonikaModel(model);
};

const withTeltonikaSmsPrefix = (command: string): string => (
  command.startsWith('  ') ? command : `  ${command}`
);

const getDefaultGpsHostname = (): string => {
  const labels = window.location.hostname.split('.').filter(Boolean);
  // Keep 3 labels for second-level ccTLDs like com.br / co.uk (gestao.rastreosat.com.br -> rastreosat.com.br)
  const hasSecondLevelTld = labels.length >= 3
    && labels[labels.length - 1].length === 2
    && ['com', 'net', 'org', 'co', 'gov', 'edu', 'ind', 'eng'].includes(labels[labels.length - 2]);
  const baseLength = hasSecondLevelTld ? 3 : 2;
  const baseHostname = labels.length >= baseLength ? labels.slice(-baseLength).join('.') : window.location.hostname;
  return `gps.${baseHostname}`;
};

const getSetServerCommand = (device: Device, apn: string, gpsServer: string, stockDevice?: DeviceStock): string => {
  const hostname = gpsServer || getDefaultGpsHostname();
  const model = (device.model || stockDevice?.model || '').toLowerCase();
  if (isTeltonikaModel(model) || isTeltonikaStockDevice(stockDevice)) {
    const teltonikaParams = [
      apn.trim() ? `2001:${apn.trim()}` : null,
      `2004:${hostname}`,
      '2005:5027',
    ].filter(Boolean);
    return `setparam ${teltonikaParams.join(';')}`;
  }
  if (/^st[0-9]/.test(model)) {
    return `ST300NTW;${device.uniqueId};02;1;${apn || ''},,,${hostname};9016;jsrastreamento.stctecnologia.com.br;9016;;`;
  }
  if (model === 'bn303') {
    return `adminip123456 ${hostname} 5001`;
  }
  return `SERVER,1,${hostname},5023,0#`;
};

const FALLBACK_COMMAND_TYPES = [
  'custom', 'positionSingle', 'positionPeriodic', 'positionStop',
  'engineStop', 'engineResume', 'alarmArm', 'alarmDisarm',
  'requestPhoto', 'reboot', 'getVersion', 'identification',
];

interface SendCommandDialogProps {
  devices?: Device[];
  trigger?: React.ReactNode;
}

export function SendCommandDialog({ devices: preselected = [], trigger }: SendCommandDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [commandType, setCommandType] = useState('setServer');
  const [customData, setCustomData] = useState('');
  const [frequency, setFrequency] = useState('');
  const [port, setPort] = useState('1');
  const [portState, setPortState] = useState('1');
  const [useSms, setUseSms] = useState(false);

  // Device picker state — only used when no devices are pre-selected
  const showPicker = preselected.length === 0;
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [lastSelectedDeviceId, setLastSelectedDeviceId] = useState<number | null>(() => {
    const stored = Number(localStorage.getItem(LAST_COMMAND_DEVICE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : null;
  });

  const { data: allDevices = [], isLoading: loadingDevices } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: open && showPicker,
    staleTime: 60000,
  });

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.toLowerCase();
    return allDevices.filter(d => d.name.toLowerCase().includes(q) || d.uniqueId.toLowerCase().includes(q));
  }, [allDevices, deviceSearch]);

  const allFilteredSelected = filteredDevices.length > 0 && filteredDevices.every(d => selectedIds.has(d.id));

  const toggleDevice = (id: number) => {
    if (!selectedIds.has(id)) {
      setLastSelectedDeviceId(id);
      localStorage.setItem(LAST_COMMAND_DEVICE_KEY, String(id));
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => { const next = new Set(prev); filteredDevices.forEach(d => next.delete(d.id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); filteredDevices.forEach(d => next.add(d.id)); return next; });
    }
  };

  // Devices that will receive the command
  const devices = showPicker ? allDevices.filter(d => selectedIds.has(d.id)) : preselected;
  const firstDevice = devices[0] ?? preselected[0]; // use first preselected for queries even in picker mode
  const isMulti = devices.length > 1;

  const { data: tenantInfo } = useQuery<{ server?: string }>({
    queryKey: ['tenant-info'],
    queryFn: async () => {
      const res = await fetch(`https://raw.githubusercontent.com/rastreosat/rastreosat.github.io/refs/heads/main/${window.location.hostname}/info.json`);
      return res.ok ? res.json() as Promise<{ server?: string }> : {};
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: deviceStockResult } = useQuery({
    queryKey: ['device-stock-by-imei', firstDevice?.uniqueId],
    queryFn: () => fetchDeviceStockPaged({ page: 1, limit: 1, search: firstDevice!.uniqueId }),
    enabled: open && !!firstDevice?.uniqueId,
    staleTime: 60000,
  });

  const simCardId = deviceStockResult?.data[0]?.sim_card_id;
  const stockDevice = deviceStockResult?.data[0];

  const { data: simCard } = useQuery({
    queryKey: ['sim-card', simCardId],
    queryFn: () => fetchSimCard(simCardId!),
    enabled: !!simCardId,
    staleTime: 60000,
  });

  const simCardApn = simCard?.apn ?? '';

  const { data: commandTypes = [] } = useQuery({
    queryKey: ['command-types', firstDevice?.id],
    queryFn: () => fetchCommandTypes(firstDevice!.id),
    enabled: open && !!firstDevice,
    staleTime: 60000,
  });

  const { data: firstPosition } = useQuery({
    queryKey: ['device-position', firstDevice?.id],
    queryFn: () => fetchDevicePosition(firstDevice!.id),
    enabled: open && !!firstDevice,
    staleTime: 60000,
  });

  const isTeltonika = firstPosition?.protocol?.toLowerCase().startsWith('teltonika')
    || isTeltonikaStockDevice(stockDevice);

  const availableTypes = [
    'setServer',
    'custom',
    ...(commandTypes.length > 0 ? commandTypes.map(ct => ct.type) : FALLBACK_COMMAND_TYPES).filter(t => t !== 'custom'),
  ];

  const gpsServer = tenantInfo?.server ?? '';

  useEffect(() => {
    if (!open) return;
    if (commandType === 'setServer' && firstDevice) {
      setCustomData(getSetServerCommand(firstDevice, simCardApn, gpsServer, stockDevice));
    } else if (commandType !== 'custom') {
      setCustomData('');
    }
  }, [open, commandType, simCardApn, gpsServer, firstDevice, stockDevice]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && showPicker && lastSelectedDeviceId) {
      setSelectedIds(new Set([lastSelectedDeviceId]));
    }
    if (!isOpen) {
      setCommandType('setServer');
      setCustomData('');
      setFrequency('');
      setPort('1');
      setPortState('1');
      setUseSms(false);
      setDeviceSearch('');
      setSelectedIds(new Set());
    }
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (devices.length === 0) throw new Error(t('commands.noDevicesSelected', { defaultValue: 'Select at least one device' }));

      const sendType = commandType === 'setServer' ? 'custom' : commandType;

      const resolvedDevices = useSms
        ? await Promise.all(devices.map(async (device) => {
            if (device.phone) return device;
            try {
              const stockResult = await fetchDeviceStockPaged({ page: 1, limit: 1, search: device.uniqueId });
              const sid = stockResult?.data[0]?.sim_card_id;
              if (!sid) return device;
              const sim = await fetchSimCard(sid);
              if (!sim?.phone_number) return device;
              return await updateDevice({ ...device, phone: sim.phone_number });
            } catch { return device; }
          }))
        : devices;

      const phonesAdded = resolvedDevices.filter((d, i) => !devices[i].phone && d.phone).length;
      if (phonesAdded > 0) toast.info(t('devices.phoneAutoUpdated', { count: phonesAdded, defaultValue: `Phone auto-filled from device stock for ${phonesAdded} device(s).` }));

      return Promise.all(resolvedDevices.map(async device => {
        const attributes: Record<string, unknown> = {};
        let commandData: string | undefined;
        if (COMMANDS_WITH_DATA.includes(commandType)) {
          commandData = useSms && isTeltonika ? withTeltonikaSmsPrefix(customData) : customData;
          attributes.data = commandData;
        }
        if (COMMANDS_WITH_FREQUENCY.includes(commandType) && frequency) attributes.frequency = Number(frequency);
        if (COMMANDS_WITH_PORT.includes(commandType)) {
          attributes.index = Number(port);
          attributes.data = portState;
          commandData = `port=${port} state=${portState}`;
        }

        const loggedCommand = await logCommand({
          traccar_device_id: device.id,
          device_name: device.name,
          command_type: commandType,
          command_data: commandData ?? null,
          via_sms: !!useSms,
          phone_number: useSms ? device.phone || null : null,
        });
        console.info('[commands] pending command saved before sending', {
          commandId: loggedCommand.id,
          deviceId: device.id,
          commandType,
        });

        return sendCommand({ deviceId: device.id, type: sendType, textChannel: useSms || undefined, attributes });
      }));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commands'] });
      toast.success(isMulti ? t('devices.commandSentMulti', { count: devices.length }) : t('devices.commandSent'));
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t('devices.commandError'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commandType) return;
    mutation.mutate();
  };

  const dialogTitle = showPicker
    ? t('devices.sendCommand')
    : `${t('devices.sendCommand')} — ${isMulti ? t('devices.selectedCount', { count: preselected.length }) : preselected[0]?.name}`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Terminal className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      {open && <DialogContent className={showPicker ? 'sm:max-w-[680px] max-h-[90vh] flex flex-col' : 'sm:max-w-[440px]'}>
        <form onSubmit={handleSubmit} className={showPicker ? 'flex flex-col flex-1 min-h-0' : undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="h-4 w-4" />
              {dialogTitle}
            </DialogTitle>
          </DialogHeader>

          <div className={`grid gap-4 py-4 ${showPicker ? 'flex-1 min-h-0 flex flex-col' : ''}`}>

            {/* Device picker */}
            {showPicker && (
              <div className="flex flex-col gap-2 min-h-0">
                <Label>{t('commands.selectDevices', { defaultValue: 'Devices' })}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('common.search')}
                    value={deviceSearch}
                    onChange={e => setDeviceSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <ScrollArea className="h-44 border rounded-md">
                  {loadingDevices ? (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground py-8">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredDevices.length > 0 && (
                        <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer" onClick={toggleAll}>
                          <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} onClick={e => e.stopPropagation()} className="h-4 w-4 cursor-pointer accent-primary" />
                          <span className="text-xs text-muted-foreground font-medium">
                            {t('common.selectAll', { defaultValue: 'Select all' })} ({filteredDevices.length})
                          </span>
                        </div>
                      )}
                      {filteredDevices.map(device => (
                        <div key={device.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer" onClick={() => toggleDevice(device.id)}>
                          <input type="checkbox" checked={selectedIds.has(device.id)} onChange={() => toggleDevice(device.id)} onClick={e => e.stopPropagation()} className="h-4 w-4 cursor-pointer accent-primary" />
                          <span className="text-sm font-medium flex-1 truncate">{device.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{device.uniqueId}</span>
                        </div>
                      ))}
                      {filteredDevices.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-6">{t('common.noData')}</p>
                      )}
                    </div>
                  )}
                </ScrollArea>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                      {devices.slice(0, 3).map(device => (
                        <Badge key={device.id} variant="secondary" className="max-w-40 truncate">
                          {device.name}
                        </Badge>
                      ))}
                      {devices.length > 3 && (
                        <Badge variant="outline">+{devices.length - 3}</Badge>
                      )}
                    </div>
                    <button type="button" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5" onClick={() => setSelectedIds(new Set())}>
                      <X className="h-3 w-3" />{t('devices.clearSelection')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Command type */}
            <div className="grid gap-2">
              <Label>{t('devices.commandType')}</Label>
              <Select value={commandType} onValueChange={setCommandType}>
                <SelectTrigger>
                  <SelectValue placeholder={t('devices.selectCommandType')} />
                </SelectTrigger>
                <SelectContent>
                  {availableTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'setServer'
                        ? t('devices.preset_setServer')
                        : t(`devices.command_${type}`, { defaultValue: type })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Command data */}
            {COMMANDS_WITH_DATA.includes(commandType) && (
              <div className="grid gap-2">
                <Label htmlFor="cmd-data">{t('devices.commandData')}</Label>
                {commandType === 'custom' && isTeltonika && (
                  <div className="flex flex-wrap gap-1.5">
                    {TELTONIKA_PRESETS.map(p => (
                      <button
                        key={p.command}
                        type="button"
                        onClick={() => setCustomData(p.command)}
                        className={`text-xs px-2 py-0.5 rounded border font-mono transition-colors ${
                          customData === p.command
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-muted hover:bg-muted/80 border-border'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
                <Textarea
                  id="cmd-data"
                  value={customData}
                  onChange={(e) => setCustomData(e.target.value)}
                  placeholder={t('devices.commandDataPlaceholder')}
                  rows={3}
                  className="font-mono text-sm"
                />
              </div>
            )}

            {COMMANDS_WITH_FREQUENCY.includes(commandType) && (
              <div className="grid gap-2">
                <Label htmlFor="cmd-frequency">{t('devices.commandFrequency')}</Label>
                <div className="relative">
                  <Input id="cmd-frequency" type="number" min="1" value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="30" className="pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">s</span>
                </div>
              </div>
            )}

            {COMMANDS_WITH_PORT.includes(commandType) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cmd-port">{t('devices.commandPort')}</Label>
                  <Input id="cmd-port" type="number" min="1" value={port} onChange={(e) => setPort(e.target.value)} />
                </div>
                <div className="grid gap-2">
                  <Label>{t('devices.commandPortState')}</Label>
                  <Select value={portState} onValueChange={setPortState}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t('common.on')}</SelectItem>
                      <SelectItem value="0">{t('common.off')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pb-2">
            <Switch id="sms-toggle" checked={useSms} onCheckedChange={setUseSms} />
            <Label htmlFor="sms-toggle">{t('devices.sendViaSms')}</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending || (showPicker && selectedIds.size === 0) || !commandType}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('devices.sendCommand')}
              {showPicker && selectedIds.size > 0 && ` (${selectedIds.size})`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>}
    </Dialog>
  );
}
